import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../Firebase/firebaseConfig';
import { useTranslation } from 'react-i18next';
import { Alert, Modal } from 'react-bootstrap';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '../Courses_details/CourseDetail.css'; // shared header / layout / enrollment card
import './DiplomaDetails.css';

const DiplomaDetails = () => {
  const { diplomaId } = useParams();
  const navigate = useNavigate();
  const [diploma, setDiploma] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false); // State for Modal
  const { t, i18n } = useTranslation();

  // Helper function to format duration
  const formatDuration = (duration) => {
    if (duration && duration.amount && duration.unit) {
      return `${duration.amount} ${t(duration.unit)}`;
    }
    return '';
  };

  useEffect(() => {
    const fetchDiplomaAndCourses = async () => {
      try {
        // Fetch diploma details
        const diplomaDoc = await getDoc(doc(db, 'diplomas', diplomaId));
        if (diplomaDoc.exists()) {
          setDiploma(diplomaDoc.data());
        } else {
          setError(t('diplomaNotFound'));
        }

        // Fetch courses
        const coursesSnapshot = await getDocs(collection(db, 'courses'));
        const coursesData = coursesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCourses(coursesData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(t('failedToLoadDiploma'));
        setLoading(false);
      }
    };

    fetchDiplomaAndCourses();
  }, [diplomaId, t]);

  useEffect(() => {
    // Set text direction based on current language
    if (i18n.language === 'ar') {
      document.body.dir = 'rtl';
      document.body.lang = 'ar';
    } else {
      document.body.dir = 'ltr';
      document.body.lang = 'en';
    }
  }, [i18n.language]);

  const getDiplomaContent = (field) => {
    if (diploma[field]) {
      return diploma[field][i18n.language] || diploma[field]['en'] || '';
    }
    return '';
  };

  const getCourseById = (courseId) => courses.find((course) => course.id === courseId);

  // Handle Enroll Button Click
  const handleEnrollClick = () => {
    if (diploma.enrollmentOpen) {
      navigate(`/enroll/diploma/${diplomaId}`);
    } else {
      setShowModal(true);
    }
  };

  const handleCloseModal = () => setShowModal(false);

  if (loading) {
    return (
      <main className="course-details-container">
        <div className="loader-container">
          <div className="spinner"></div>
          <p>{t('loading')}</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="course-details-container">
        <div className="container error-container">
          <Alert variant="danger">{error}</Alert>
          <button className="btn btn-outline-primary" onClick={() => navigate(-1)}>{t('goBack', 'Back')}</button>
        </div>
      </main>
    );
  }

  const isOpen = !!diploma.enrollmentOpen;
  const name = getDiplomaContent('name');
  const semesters = Array.isArray(diploma.semesters) ? diploma.semesters : [];
  const totalCourses = semesters.reduce((n, s) => n + (Array.isArray(s.courses) ? s.courses.length : 0), 0);
  const duration = formatDuration(diploma.duration);

  const ledger = [
    ['far fa-clock', t('duration'), duration || t('notSpecified')],
    ['far fa-calendar', t('semesters', 'Semesters'), semesters.length || t('notSpecified')],
    ['far fa-bookmark', t('courses'), totalCourses || t('notSpecified')],
  ];

  return (
    <main className="course-details-container">
      {/* Hero */}
      <header className="cd-hero">
        <div className="container">
          <button className="cd-back" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left"></i> {t('goBack', 'Back')}
          </button>
          <span className="cd-hero__eyebrow">{t('diploma', 'Diploma')}</span>
          <h1 className="course-title diploma-title">{name}</h1>
          <ul className="cd-chips">
            {duration && <li><i className="far fa-clock"></i> {duration}</li>}
            {semesters.length > 0 && <li><i className="far fa-calendar"></i> {semesters.length} {t('semesters', 'Semesters')}</li>}
            <li className={`cd-chip-status ${isOpen ? 'open' : 'closed'}`}>
              {isOpen ? t('enrollmentOpen', 'Enrollment open') : t('enrollmentClosed')}
            </li>
          </ul>
        </div>
      </header>

      <div className="container cd-layout">
        {/* Main column */}
        <div className="cd-main">
          <section className="cd-section">
            <h3>{t('overview')}</h3>
            <p className="cd-lead diploma-description">{getDiplomaContent('description') || t('noOverview')}</p>
          </section>

          <section className="cd-section">
            <h3>{t('curriculum', 'Curriculum')}</h3>
            {semesters.length > 0 ? (
              <div className="dd-semesters">
                {semesters.map((semester, index) => (
                  <article className="dd-semester" key={index}>
                    <header className="dd-semester__head">
                      <span className="dd-semester__num">{String(index + 1).padStart(2, '0')}</span>
                      <h4 className="semester-title">{t('semester')} {index + 1}</h4>
                    </header>
                    {Array.isArray(semester.courses) && semester.courses.length > 0 ? (
                      <ul className="dd-courses">
                        {semester.courses.map((courseId) => {
                          const course = getCourseById(courseId);
                          return (
                            <li key={courseId}>
                              <span className="course-name">
                                {course ? (course.title?.[i18n.language] || course.title?.en) : t('courseNotFound')}
                              </span>
                              {course && (
                                <button className="dd-course-link" onClick={() => navigate(`/course-details/${courseId}`)}>
                                  {t('courseDetails')} <i className="fas fa-arrow-right"></i>
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="no-content">{t('noCoursesAssigned')}</p>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <p className="no-content">{t('noSemesters')}</p>
            )}
          </section>
        </div>

        {/* Sticky enrollment card */}
        <aside className="cd-aside">
          <div className="cd-card">
            <h3>{t('diplomaDetails', 'Diploma details')}</h3>
            <dl className="cd-ledger">
              {ledger.map(([icon, label, value]) => (
                <div className="cd-ledger__row" key={label}>
                  <dt><i className={icon}></i> {label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
              <div className="cd-ledger__row">
                <dt><i className="fas fa-lock"></i> {t('enrollment', 'Enrollment')}</dt>
                <dd className={`cd-status ${isOpen ? 'open' : 'closed'}`}>
                  {isOpen ? t('enrollmentOpen', 'Enrollment open') : t('enrollmentClosed')}
                </dd>
              </div>
            </dl>

            <button
              className={`btn btn-primary enroll-button ${isOpen ? '' : 'is-closed'}`}
              onClick={handleEnrollClick}
              aria-label={`${t('enrollNow', 'Enroll now')} ${name}`}
            >
              {isOpen ? t('enrollNow', 'Enroll now') : t('enrollClosed', 'Enrollment closed')}
            </button>
            {!isOpen && (
              <button className="cd-contact" onClick={() => navigate('/contact')}>
                {t('contactUs')} <i className="fas fa-arrow-right"></i>
              </button>
            )}
          </div>
        </aside>
      </div>

      {/* Enrollment Closed Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('enrollmentClosed')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('enrollmentClosedMessage')}</p>
          <p>{t('contactUs')}</p>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-primary" onClick={handleCloseModal}>{t('close')}</button>
          <button className="btn btn-primary" onClick={() => navigate('/contact')}>{t('contactUs')}</button>
        </Modal.Footer>
      </Modal>
    </main>
  );
};

export default DiplomaDetails;
